import { useEffect, useRef } from 'react';
import { useGameStore } from '@/state/gameStore';

/**
 * Phase 13.8 — Background-friendly game clock.
 *
 * Earlier phases drove the tick on `requestAnimationFrame`, which freezes
 * when the tab is hidden. The simulation should keep running so missions
 * progress even with the tab in the background. We now use `setInterval`
 * (10 Hz) for the logic tick. Browsers throttle background intervals to
 * ~1 Hz minimum, but the tick handler computes `delta * speedMultiplier`
 * from `performance.now()`, so longer intervals still advance the right
 * amount of game-time.
 *
 * Map animations stay on rAF (vehicle position interpolation, etc.) — this
 * hook does NOT replace that.
 */
const TICK_INTERVAL_MS = 100;

/** Cap a single delta so a long-suspended tab doesn't fast-forward years. */
const MAX_SINGLE_DELTA_MS = 5 * 60_000; // 5 real minutes

export function useGameClock() {
  const tick = useGameStore((s) => s.tick);
  const lastTs = useRef<number>(0);
  const announcedHiddenOnce = useRef(false);
  const pushToast = useGameStore((s) => s.pushToast);

  useEffect(() => {
    lastTs.current = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      const delta = now - lastTs.current;
      lastTs.current = now;
      if (delta <= 0) return;
      tick(Math.min(delta, MAX_SINGLE_DELTA_MS));
    }, TICK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [tick]);

  // Phase 13.8 — One-shot info toast the first time the user backgrounds
  // the tab. Confirms the simulation keeps running.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden && !announcedHiddenOnce.current) {
        announcedHiddenOnce.current = true;
        pushToast({
          kind: 'info',
          text: 'Spiel läuft im Hintergrund weiter.',
        });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [pushToast]);
}
